import _ from 'lodash';
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { debounceTime, filter, map, take, takeUntil, throttleTime } from 'rxjs/operators';
import { selectDonationCampaignList } from 'app/campaign/features/campaign.selectors';
import { CampaignService } from 'app/campaign/services/campaign.service';
import { SlideSchema, SponsorSchema, VirtualEventSchema } from '../../structure/virtual-event';
import { loadEventCampaignList } from 'app/campaign/features/campaign.actions';
import { fromEvent, Subject } from 'rxjs';
import { ImageService } from 'app/core/services/image.service';
import { DomSanitizer } from '@angular/platform-browser';
import { clearVirtualEventData, createVirtualEvent, loadVirtualEvent, updateSlideSearch, updateSponsorSearch, updateVirtualEvent } from '../../features/data/data.actions';
import { selectVirtualEventData } from '../../features/data/data.selectors';
import * as moment from 'moment-timezone';
import * as DialogActions from 'app/dialog/features/dialog.actions';
import { MdbTabChange } from 'mdb-angular-ui-kit/tabs/tabs.component';

@Component({
  selector: 'app-virtual-event-editor',
  templateUrl: './virtual-event-editor.component.html',
  styleUrls: ['./virtual-event-editor.component.scss']
})
export class VirtualEventEditorComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() public id: string = null;
  @Input() public token: string = null;

  @Input()
  public title = 'ngx-editorjs Material Field';

  public step: number = 0;
  public prevStep:number = 0;

  public selectedVirtualEvent = this.store.pipe(
    select(selectVirtualEventData)
  );

  public virtualEvent: UntypedFormGroup = null;
  public virtualEventSponsor: UntypedFormGroup = null;
  public virtualEventSlide: UntypedFormGroup = null;
  public initialized = true;
  public submitted = false;

  public sponsorsSearch: UntypedFormControl = new UntypedFormControl('', []);
  public sponsorMode: string = 'list';
  public selectedSponsor: UntypedFormGroup = null;

  public slidesSearch: UntypedFormControl = new UntypedFormControl('', []);
  public slideMode: string = 'list';
  public selectedSlide: UntypedFormGroup = null;

  public campaigns$: any = this.store.pipe(
    select(selectDonationCampaignList),
    map(list => {
      return _.filter(list, { status: 0 });
    })
  );

  public accordion = {
    info: { expanded: false },
    stream: { expanded: false },
    settings: { expanded: false },
    music: { expanded: false }
  }

  public eventTypeList = [
    { id: 1, label: 'Open', value: 0 },
    { id: 2, label: 'Closed', value: 1 }
  ];

  public streamProviderList: any[] = [
    { id: 1, label: "Other", value: 0 },
    { id: 2, label: "YouTube", value: 1 },
    { id: 3, label: "Facebook", value: 2 },
    { id: 4, label: "Twitch", value: 3 },
    { id: 5, label: "Paybee", value: 4 }
  ];

  public pledgeBehaviourList = [
    { id: 1, label: 'Pledge Other', value: 0 },
    { id: 2, label: 'Pledge More', value: 1 }
  ];

  public timeZoneList = [];

  public inviteMessageObject = new UntypedFormControl([]);
  public confirmMessageObject = new UntypedFormControl([]);

  public modules = {
    table: true,
    imageResize: {
      displaySize: true
    },
    clipboard: true,
    toolbar: [
      ['button'],
      ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],

      [{ 'size': ['10px' , false, '18px', '32px'] }],  // custom dropdown
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

      [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['link', 'image']
    ]
  };

  public takeUntilSubject: Subject<any> = new Subject();


  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private campaignService: CampaignService,
    public imageService: ImageService,
    private domSanitizer: DomSanitizer,
    private element: ElementRef,
  ) {
  }
  onTabChange(event: MdbTabChange):void {
    this.prevStep = this.step
    this.step = event.index


    if(event.index == 1){
      this.onValidate()
    }
    if(event.index == 2){
      this.onValidate()
    }
  }
  ngOnInit(): void {
    this.virtualEvent = this.fb.group({});
    this.virtualEventSponsor = this.fb.group({});
    this.virtualEventSlide = this.fb.group({});

    this.campaignService.generateForm(this.virtualEvent, VirtualEventSchema);
    this.campaignService.generateForm(this.virtualEventSponsor, SponsorSchema);
    this.campaignService.generateForm(this.virtualEventSlide, SlideSchema);

    this.store.dispatch({
      type: loadEventCampaignList.type,
      payload: this.token
    });

    if(this.id) {
      this.initialized = false;
      this.store.dispatch({
        type: loadVirtualEvent.type,
        payload: this.id
      });
    } else {
      const timestamp = this.virtualEvent.get('startDateTimeGmtUnix').value * 1000;
      const zones = moment.tz.names();
      this.timeZoneList = _.chain(zones).map((name, index) => {
        const offset = moment.tz.zone(name).utcOffset(timestamp);
        const gmt = `GMT ${moment.tz(timestamp, name).format('Z')}`;
        let abbr = moment.tz(timestamp, name).format('z');
        if(abbr.match(/[\+\-][0-9]+/) !== null) {
          abbr = gmt;
        }
        const label = `(${gmt}) ${name} - ${abbr}`;
        return { index, label, value: name, offset };
      }).sortBy('offset').value();
    }

    this.selectedVirtualEvent.pipe(
      filter(virtualEvent => {
        const id = _.get(virtualEvent, 'id', null);
        return !_.isNull(id)
      }),
      takeUntil(this.takeUntilSubject)
    ).subscribe((virtualEvent: any) => {
      this.resetVirtualEvent(this.virtualEvent);
      this.campaignService.initializeForm(this.virtualEvent, virtualEvent, VirtualEventSchema);

      this.initialized = true;

      const timestamp = virtualEvent.startDateTimeGmtUnix * 1000;
      const zones = moment.tz.names();
      this.timeZoneList = _.chain(zones).map((name, index) => {
        const offset = moment.tz.zone(name).utcOffset(timestamp);
        const gmt = `GMT ${moment.tz(timestamp, name).format('Z')}`;
        let abbr = moment.tz(timestamp, name).format('z');
        if(abbr.match(/[\+\-][0-9]+/) !== null) {
          abbr = gmt;
        }
        const label = `(${gmt}) ${name} - ${abbr}`;
        return { index, label, value: name, offset };
      }).sortBy('offset').value();
    });

    const timeZoneData = this.virtualEvent.get('timeZoneData');
    timeZoneData.get('name').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(value => {
      const zone = moment.tz.zone(value);
      const abbrControl = timeZoneData.get('abbr');

      const timestamp = this.virtualEvent.get('startDateTimeGmtUnix').value * 1000;
      const abbr = zone.abbr(timestamp);

      abbrControl.setValue(abbr);
    });

    timeZoneData.get('abbr').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(abbr => {
			const abbrControl = timeZoneData.get('abbr');
      const nameControl = timeZoneData.get('name');
      const offsetControl = timeZoneData.get('offset');

      const zone = moment.tz.zone(nameControl.value);

      const index = _.findIndex(zone.abbrs, value => _.isEqual(value, abbr));
      const offset = zone.offsets[index] * -60;

			offsetControl.setValue(offset);

      const matches = abbr.match(/[\+\-][0-9]+/);
      const abbrValue = _.isArray(matches) ? `GMT ${matches[0]}`: abbr;

      this.virtualEvent.get('timeZoneAbbr').setValue(abbrValue);
      this.virtualEvent.get('timeZone').setValue(offset);

      abbrControl.setValue(abbrValue, { emitEvent: false });
    });

    this.virtualEvent.get('startDateTimeGmtUnix').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      filter(() => {
        const nameControl = timeZoneData.get('name');
        return !_.isNull(nameControl.value)
      }),
      filter(() => {
        const abbrControl = timeZoneData.get('abbr');
        return !_.isNull(abbrControl.value)
      }),
      map(value => value * 1000),
      takeUntil(this.takeUntilSubject)
    ).subscribe(value => {
      const nameControl = timeZoneData.get('name');
      const abbrControl = timeZoneData.get('abbr');
      const zone = moment.tz.zone(nameControl.value);
      const abbr = zone.abbr(value);
      abbrControl.setValue(abbr);

      const timestamp = this.virtualEvent.get('startDateTimeGmtUnix').value * 1000;
      const zones = moment.tz.names();
      this.timeZoneList = _.chain(zones).map((name, index) => {
        const offset = moment.tz.zone(name).utcOffset(timestamp);
        const gmt = `GMT ${moment.tz(timestamp, name).format('Z')}`;
        let abbr = moment.tz(timestamp, name).format('z');
        if(abbr.match(/[\+\-][0-9]+/) !== null) {
          abbr = gmt;
        }
        const label = `(${gmt}) ${name} - ${abbr}`;
        return { index, label, value: name, offset };
      }).sortBy('offset').value();
    });

    this.virtualEvent.get('imageFile').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(value => {
      const windowURL = window.URL || window.webkitURL;
      const url = windowURL.createObjectURL(value);
      this.imageService.cropper({
        url: this.domSanitizer.bypassSecurityTrustUrl(url),
        id: 'virtual-event-image',
        name: value.name,
        type: value.type,
      });
    });

    this.imageService.imageDataUrlSubject.pipe(
      filter(({ id }) => id === 'virtual-event-image'),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      this.virtualEvent.get('imageDataUrl').setValue(result);
    });

    this.imageService.imageSubject.pipe(
      filter(({ id }) => id === 'virtual-event-image'),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      if(result === false) {
        this.resetImage();
      } else {
        this.virtualEvent.get('imageUrl').setValue(result.imageUrl);
      }
    });

    this.virtualEventSponsor.get('imageFile').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(value => {
      const windowURL = window.URL || window.webkitURL;
      const url = windowURL.createObjectURL(value);
      this.imageService.cropper({
        url: this.domSanitizer.bypassSecurityTrustUrl(url),
        id: 'sponsor-image',
        name: value.name,
        type: value.type,
      });
    });

    this.imageService.imageDataUrlSubject.pipe(
      filter(({ id }) => id === 'sponsor-image'),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      this.virtualEventSponsor.get('imageDataUrl').setValue(result);
    });

    this.imageService.imageSubject.pipe(
      filter(({ id }) => id === 'sponsor-image'),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      if(result === false) {
        this.resetImage();
      } else {
        this.virtualEventSponsor.get('imageUrl').setValue(result.imageUrl);
      }
    });

    this.virtualEventSlide.get('imageFile').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(value => {
      const windowURL = window.URL || window.webkitURL;
      const url = windowURL.createObjectURL(value);
      this.imageService.cropper({
        url: this.domSanitizer.bypassSecurityTrustUrl(url),
        id: 'slide-image',
        name: value.name,
        type: value.type,
      });
    });

    this.imageService.imageDataUrlSubject.pipe(
      filter(({ id }) => id === 'slide-image'),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      this.virtualEventSlide.get('imageDataUrl').setValue(result);
    });

    this.imageService.imageSubject.pipe(
      filter(({ id }) => id === 'slide-image'),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      if(result === false) {
        this.resetImage();
      } else {
        this.virtualEventSlide.get('imageUrl').setValue(result.imageUrl);
      }
    });

    this.campaignService.configGroup(this.virtualEvent, this.virtualEvent, 'additionalInfoRequired', [
      { name: 'additionalInfoName', defaultValue: "", validators: [Validators.required] },
    ], this.takeUntilSubject);

    this.campaignService.configGroup(this.virtualEvent, this.virtualEvent, 'defaultDonateCampaignRequired', [
      { name: 'defaultDonateCampaignId', defaultValue: "", validators: [Validators.required] },
    ], this.takeUntilSubject);

    this.sponsorsSearch.valueChanges.pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe(payload => {
      this.store.dispatch({
        type: updateSponsorSearch.type,
        payload
      });
    });

    this.slidesSearch.valueChanges.pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe(payload => {
      this.store.dispatch({
        type: updateSlideSearch.type,
        payload
      });
    });
  }

  ngAfterViewInit() {
    this.virtualEvent.get('lobbyMusic').valueChanges.pipe(
      filter(value => !_.isNull(value) && !_.isEmpty(value)),
      throttleTime(1000),
      takeUntil(this.takeUntilSubject)
    ).subscribe(() => {
      const lobbyMusicAudio = window['$']('#lobbyMusicAudio');
      if(lobbyMusicAudio.length > 0) {
        lobbyMusicAudio[0].load();
      }
    });

    this.virtualEvent.get('endMusic').valueChanges.pipe(
      filter(value => !_.isNull(value) && !_.isEmpty(value)),
      throttleTime(1000),
      takeUntil(this.takeUntilSubject)
    ).subscribe(() => {
      const endMusicAudio = window['$']('#endMusicAudio');
      if(endMusicAudio.length > 0) {
        endMusicAudio[0].load();
      }
    });
  }

  ngOnDestroy() {
    this.store.dispatch({
      type: clearVirtualEventData.type
    });

    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();
  }

  resetVirtualEvent(formGroup) {
    _.forEach(formGroup.controls, (control, key) => {
      if(control instanceof UntypedFormGroup) {
        this.resetVirtualEvent(control);
      } else if(control instanceof UntypedFormArray) {
        while (control.length !== 0) {
          control.removeAt(0)
        }
      } else if(control instanceof UntypedFormControl) {
        switch(key) {
          case 'inviteMessage':
          case 'confirmMessage':
            control.reset('');
            break;
          default:
            control.reset();
        }
      }
    });
  }

  resetImage() {
    this.virtualEvent.get('imageFile').setValue(null);
    this.virtualEvent.get('imageDataUrl').setValue('/images/blank_2x1.png');
    this.virtualEvent.get('imageUrl').setValue('/images/blank_2x1.png');
  }

  resetSponsorImage() {
    this.virtualEventSponsor.get('imageFile').setValue(null);
    this.virtualEventSponsor.get('imageDataUrl').setValue('/images/blank_2x1.png');
    this.virtualEventSponsor.get('imageUrl').setValue('/images/blank_2x1.png');
  }

  resetSlideImage() {
    this.virtualEventSlide.get('imageFile').setValue(null);
    this.virtualEventSlide.get('imageDataUrl').setValue('/images/blank_2x1.png');
    this.virtualEventSlide.get('imageUrl').setValue('/images/blank_2x1.png');
  }

  onCancel(): void {
  }

  onSave(): void {
    const valid = this.onValidate();
    if(valid === false) return;

    const requestObject = this.campaignService.generateRequestObject(this.virtualEvent, VirtualEventSchema);
    this.store.dispatch({
      type: this.id ? updateVirtualEvent.type: createVirtualEvent.type,
      payload: requestObject,
      redirect: true
    });
  }

  onValidate(): boolean {
    this.submitted = true;
    if(this.virtualEvent.valid) {
      return true;
    }
    this.virtualEvent.markAllAsTouched();

    const firstInvalidControl: HTMLElement = this.element.nativeElement.querySelector(".ng-invalid");
    const panel = _.get(firstInvalidControl.dataset, 'panel', null);
    if(!_.isNull(panel)) {
      _.set(this.accordion, `${panel}.expanded`, true);
    }

    window.scroll({
      top: this.getTopOffset(firstInvalidControl),
      left: 0,
      behavior: "smooth"
    });

    fromEvent(window, "scroll").pipe(
      debounceTime(100),
      take(1),
      takeUntil(this.takeUntilSubject)
    ).subscribe(() => firstInvalidControl.focus());

    return false;
  }

  private getTopOffset(controlEl: HTMLElement): number {
    const labelOffset = 50;
    return controlEl.getBoundingClientRect().top + window.scrollY - labelOffset;
  }

  resetSponsorsSearch() {
    this.sponsorsSearch.setValue('');
  }

  resetSlidesSearch() {
    this.slidesSearch.setValue('');
  }

  onChangeSponsorMode(mode) {
    this.resetVirtualEvent(this.virtualEventSponsor);
    this.campaignService.initializeForm(this.virtualEventSponsor, {}, SponsorSchema);
    this.sponsorMode = mode;
  }

  onSaveSponsor() {
    if(this.virtualEventSponsor.invalid) {
      this.virtualEventSponsor.markAllAsTouched();
      return;
    }

    const form = this.fb.group({});
    this.campaignService.generateForm(form, SponsorSchema);
    this.campaignService.initializeForm(form, this.virtualEventSponsor.value, SponsorSchema);

    const sponsors = this.virtualEvent.get('sponsors') as UntypedFormArray;
    sponsors.push(form);

    this.onChangeSponsorMode('list');
  }

  onChangeSlideMode(mode) {
    this.resetVirtualEvent(this.virtualEventSlide);
    this.campaignService.initializeForm(this.virtualEventSlide, {}, SlideSchema);
    this.slideMode = mode;
  }

  onSaveSlide() {
    if(this.virtualEventSlide.invalid) {
      this.virtualEventSlide.markAllAsTouched();
      return;
    }

    const form = this.fb.group({});
    this.campaignService.generateForm(form, SlideSchema);
    this.campaignService.initializeForm(form, this.virtualEventSlide.value, SlideSchema);

    const slides = this.virtualEvent.get('slides') as UntypedFormArray;
    slides.push(form);

    this.onChangeSlideMode('list');
  }

  onChangeMusic(type) {
    switch(type) {
      case 'lobby':
        return window['showUploadMusicModal'](this.virtualEvent, "lobbyMusic");
      case 'end':
        return window['showUploadMusicModal'](this.virtualEvent, "endMusic");
    }
  }

  onResetMusic(type) {
    switch(type) {
      case 'lobby':
        return this.virtualEvent.get('lobbyMusic').setValue('/images/bensound-elevate.mp3');
      case 'end':
        return this.virtualEvent.get('endMusic').setValue('/images/bensound-happyrock.mp3');
    }
  }

	onClickHelp() {
    const action = DialogActions.openDialog({
      payload: {
        name: "showAnonymous",
        options: {
          size: 'small',
          modalClass:'modal--paybee modal-sm',
          containerClass:'mdb-modal-backdrop-paybee'
        },
      }
    });
    this.store.dispatch(action);
  }
}
